import React, { useEffect, useMemo, useState, useRef } from 'react';
import LeftBar from '../../Components/LeftBar';
import Dashnav from '../../Components/Dashnav';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAdd, faArrowLeft, faCalendarDays, faClock, faTrashCan, faUserEdit, faUsers } from '@fortawesome/free-solid-svg-icons';
import Table from 'react-bootstrap/Table';
import Modal from 'react-bootstrap/Modal';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import { useNavigate } from 'react-router-dom';
import { db } from '../../firebase';
import swal from 'sweetalert';
import {
  doc,
  getDoc,
  collection,
  query,
  where,
  getDocs,
  addDoc,
  deleteDoc,
  updateDoc
} from 'firebase/firestore';
import samleImage from '../../Images/place.jpg';
import profilePictureContImage from '../../Images/place.jpg'; // fallback

function ViewAllClasses() {
  const navigate = useNavigate();
  const receivedId = decodeURIComponent(window.location.search);
  const roomID = receivedId.substring(1);

  const [classData, setClassData] = useState(null);
  const [students, setStudents] = useState([]);
  const [instructorName, setInstructorName] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [deletingClass, setDeletingClass] = useState(false);

  const [searchValue, setSearchValue] = useState('');
  const [results, setResults] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [instructor, setInstructor] = useState(null);
  const [studentSearch, setStudentSearch] = useState('');

  const [showInstructorModal, setShowInstructorModal] = useState(false);
  const [instructorSearch, setInstructorSearch] = useState('');
  const [instructorResults, setInstructorResults] = useState([]);

  const searchResultsRef = useRef(null);

  // Fetch class info
  useEffect(() => {
    const fetchClassData = async () => {
      setLoading(true);

      try {
        const classDocRef = doc(db, 'allclasses', roomID);
        const classSnap = await getDoc(classDocRef);

        if (classSnap.exists()) {
          const classInfo = classSnap.data();
          setClassData(classInfo);

          const studentsQuery = query(
            collection(db, 'StudentsInClass'),
            where('classID', '==', roomID)
          );
          const querySnapshot = await getDocs(studentsQuery);
          const studentList = querySnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }));
          setStudents(studentList);

          if (classInfo.instructor && typeof classInfo.instructor === 'string') {
            const instructorRef = doc(db, 'Employees', classInfo.instructor);
            const instructorSnap = await getDoc(instructorRef);
            if (instructorSnap.exists()) {
              const instructorData = { id: instructorSnap.id, ...instructorSnap.data() };
              setInstructor(instructorData);
              setInstructorName(instructorData.employeeName || '');
            }
          }

        } else {
          console.log('No such class!');
        }
      } catch (err) {
        console.error('Error fetching class:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchClassData();
  }, [roomID]);

  // Fetch all users for search
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const usersSnap = await getDocs(collection(db, 'users'));
        const allUsers = usersSnap.docs.map(doc => {
          const data = doc.data();
          return {
            profilePic: data.profilePic || profilePictureContImage,
            bio: data.bio || '',
            userStatus: data.userStatus || '',
            userId: data.userId || doc.id,
            course: data.course || '',
            userEmail: data.userEmail || ''
          };
        });
        setAllUsers(allUsers);
        setResults(allUsers);
      } catch (error) {
        console.error('Error fetching users:', error);
      }
    };

    fetchUsers();
  }, []);

  const classSummary = useMemo(() => {
    const isActive = String(classData?.isActive || '').toLowerCase().includes('active');

    return {
      isActive,
      studentsCount: students.length,
      cohortDate: classData?.intakeDate || 'Not set',
      startTime: classData?.startTime || 'Not set',
      endTime: classData?.endTime || 'Not set',
      roomId: roomID || 'N/A',
      instructorName: instructor?.employeeName || instructorName || 'N/A'
    };
  }, [classData, students.length, roomID, instructor, instructorName]);

  const filteredStudents = useMemo(() => {
    const search = studentSearch.trim().toLowerCase();

    if (!search) {
      return students;
    }

    return students.filter(student => {
      const haystack = [
        student.name,
        student.course,
        student.cohortDate,
        student.studentEmail
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

      return haystack.includes(search);
    });
  }, [students, studentSearch]);

  const handleSearchChange = event => {
    const value = event.target.value;
    setSearchValue(value);

    if (value.trim() === '') {
      setResults([]);
      return;
    }

    setResults(
      allUsers.filter(
        user =>
          user.bio.toLowerCase().includes(value.toLowerCase()) ||
          user.userEmail.toLowerCase().includes(value.toLowerCase()) ||
          user.course.toLowerCase().includes(value.toLowerCase())
      )
    );
  };

  const onSelectStudent = userId => {
    const student = results.find(r => r.userId === userId);
    setSelectedStudent(student);
    setResults([]);
    setSearchValue(student.bio);
  };

  const handleAddStudent = async () => {
    if (!selectedStudent) return;

    try {
      await addDoc(collection(db, 'StudentsInClass'), {
        name: selectedStudent.bio,
        course: selectedStudent.course,
        cohortDate: new Date().toISOString().split('T')[0],
        studentID: selectedStudent.userId,
        studentEmail: selectedStudent.userEmail,
        classID: roomID
      });

      setStudents(prev => [
        ...prev,
        {
          name: selectedStudent.bio,
          course: selectedStudent.course,
          cohortDate: new Date().toISOString().split('T')[0]
        }
      ]);

      setSelectedStudent(null);
      setSearchValue('');
      setShowModal(false);
    } catch (error) {
      console.error('Error adding student:', error);
    }
  };

  const handleRemoveStudent = async studentId => {
    swal({
      title: 'Are you sure?',
      text: 'This will remove the student from this class.',
      icon: 'warning',
      buttons: ['Cancel', 'Yes, remove'],
      dangerMode: true
    }).then(async willDelete => {
      if (willDelete) {
        try {
          await deleteDoc(doc(db, 'StudentsInClass', studentId));
          setStudents(prev => prev.filter(s => s.id !== studentId));
          swal('Removed!', 'The student has been removed from the class.', 'success');
        } catch (error) {
          console.error('Error removing student:', error);
          swal('Error', 'Failed to remove the student.', 'error');
        }
      }
    });
  };

  const handleDeleteClass = async () => {
    const confirmed = await swal({
      title: 'Delete this class?',
      text: 'This will remove the class and all students enrolled in it. This cannot be undone.',
      icon: 'warning',
      buttons: ['Cancel', 'Delete class'],
      dangerMode: true
    });

    if (!confirmed) {
      return;
    }

    setDeletingClass(true);

    try {
      const classStudentsQuery = query(
        collection(db, 'StudentsInClass'),
        where('classID', '==', roomID)
      );
      const classStudentsSnap = await getDocs(classStudentsQuery);

      await Promise.all(
        classStudentsSnap.docs.map(studentDoc =>
          deleteDoc(doc(db, 'StudentsInClass', studentDoc.id))
        )
      );

      await deleteDoc(doc(db, 'allclasses', roomID));
      swal('Deleted', 'The class has been deleted successfully.', 'success');
      navigate('/darasa');
    } catch (error) {
      console.error('Error deleting class:', error);
      swal('Error', 'Failed to delete the class.', 'error');
    } finally {
      setDeletingClass(false);
    }
  };

  // Search Employees for Instructor Switch
  const handleInstructorSearch = async e => {
    const value = e.target.value;
    setInstructorSearch(value);

    if (value.trim() === '') {
      setInstructorResults([]);
      return;
    }

    try {
      const employeesSnap = await getDocs(collection(db, 'Employees'));
      const filtered = employeesSnap.docs
        .map(doc => ({ id: doc.id, ...doc.data() }))
        .filter(emp => emp.employeeName.toLowerCase().includes(value.toLowerCase()));
      setInstructorResults(filtered);
    } catch (error) {
      console.error('Error searching employees:', error);
    }
  };

  const handleSwitchInstructor = async instructor => {
  try {
    await updateDoc(doc(db, 'allclasses', roomID), {
      instructor: instructor.id,
      instructorEmail: instructor.employeeEmail || ''
    });
    setInstructor(instructor);
    setInstructorName(instructor.employeeName);
    setClassData(prev => ({ ...prev, instructor: instructor.id }));
    setShowInstructorModal(false);
    swal('Success', 'Instructor switched successfully.', 'success');
  } catch (error) {
    console.error('Error switching instructor:', error);
    swal('Error', 'Failed to switch instructor.', 'error');
  }
};

  return (
    <div className="mainContainer">
      <LeftBar />
      <div className="theMainContainer">
        <Dashnav />

        <div className="clientsComp viewClassesPage">
          {loading ? (
            <div className="viewClassesState">
              <div className="spinner-border text-dark" role="status">
                <span className="visually-hidden">Loading class details...</span>
              </div>
              <h5>Loading class details</h5>
              <p>We are fetching the class record, instructor, and roster.</p>
            </div>
          ) : !classData ? (
            <div className="viewClassesState">
              <h5>Class not found</h5>
              <p>This class record could not be loaded.</p>
              <Button className="viewClassesGhostButton" onClick={() => navigate('/darasa')}>
                <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
                Back to classes
              </Button>
            </div>
          ) : (
            <>
              <section className="viewClassesHero">
                <div className="viewClassesHeroCopy">
                  <p className="viewClassesEyebrow">Class overview</p>
                  <h3>{classData?.classDesc || 'Class Details'}</h3>
                  <p className="viewClassesLead">
                    Review the roster, manage the instructor, and keep the class room organized from one place.
                  </p>

                  <div className="viewClassesMetaChips">
                    <span>
                      <FontAwesomeIcon icon={faUsers} />
                      {students.length} students
                    </span>
                    <span>
                      <FontAwesomeIcon icon={faCalendarDays} />
                      {classSummary.cohortDate}
                    </span>
                    <span>
                      <FontAwesomeIcon icon={faClock} />
                      {classSummary.startTime} - {classSummary.endTime}
                    </span>
                    <span className={classSummary.isActive ? 'isActive' : 'isInactive'}>
                      {classSummary.isActive ? 'Active class' : 'Inactive class'}
                    </span>
                  </div>
                </div>

                <div className="viewClassesHeroActions">
                  <Button className="viewClassesGhostButton" variant="light" onClick={() => navigate('/darasa')}>
                    <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
                    Back
                  </Button>
                  <Button className="viewClassesPrimaryButton" onClick={() => setShowModal(true)}>
                    <FontAwesomeIcon icon={faAdd} className="me-2" />
                    Add Students
                  </Button>
                  <Button className="viewClassesSecondaryButton" variant="outline-dark" onClick={() => setShowInstructorModal(true)}>
                    <FontAwesomeIcon icon={faUserEdit} className="me-2" />
                    Switch Instructor
                  </Button>
                </div>
              </section>

              <section className="viewClassesStatsGrid">
                <article className="viewClassesStatCard">
                  <span>Students</span>
                  <strong>{students.length}</strong>
                  <p>Enrolled learners currently linked to this class.</p>
                </article>
                <article className="viewClassesStatCard">
                  <span>Instructor</span>
                  <strong>{classSummary.instructorName}</strong>
                  <p>{instructor?.employeeEmail || instructor?.currentEmail || 'Instructor profile loaded from Employees.'}</p>
                </article>
                <article className="viewClassesStatCard">
                  <span>Room ID</span>
                  <strong>{roomID || 'N/A'}</strong>
                  <p>Use this identifier when tracking the daily room.</p>
                </article>
                <article className="viewClassesStatCard">
                  <span>Status</span>
                  <strong>{classSummary.isActive ? 'Active' : 'Inactive'}</strong>
                  <p>{classData?.isActive || 'Status not set'}</p>
                </article>
              </section>

              <section className="viewClassesGrid">
                <article className="viewClassesPanel viewClassesPanelWide">
                  <div className="viewClassesPanelHeader">
                    <div>
                      <p className="viewClassesEyebrow">Roster</p>
                      <h5>People in this class</h5>
                    </div>
                    <Form.Control
                      type="search"
                      className="viewClassesSearch"
                      placeholder="Search roster..."
                      value={studentSearch}
                      onChange={(event) => setStudentSearch(event.target.value)}
                    />
                  </div>

                  <div className="table-responsive viewClassesTableWrap">
                    <Table className="viewClassesTable" striped hover>
                      <thead>
                        <tr>
                          <th>Name</th>
                          <th>Cohort Date</th>
                          <th>Course</th>
                          <th>Remove</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredStudents.length > 0 ? (
                          filteredStudents.map(student => (
                            <tr key={student.id}>
                              <td>
                                <div className="viewClassesStudentName">
                                  <span>{student.name}</span>
                                  <small>{student.studentEmail || 'No email on file'}</small>
                                </div>
                              </td>
                              <td>{student.cohortDate}</td>
                              <td>{student.course}</td>
                              <td>
                                <button
                                  className="btn btn-outline-danger btn-sm"
                                  onClick={() => handleRemoveStudent(student.id)}
                                >
                                  Remove
                                </button>
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan="4" className="viewClassesEmptyRow">
                              {studentSearch ? 'No students match your search.' : 'No students have been added yet.'}
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </Table>
                  </div>
                </article>

                <aside className="viewClassesPanel viewClassesPanelSide">
                  <div className="viewClassesInstructorCard">
                    <div className="viewClassesInstructorAvatar">
                      <img src={instructor?.profilePic || samleImage} alt="Instructor profile" />
                    </div>
                    <div className="viewClassesInstructorCopy">
                      <p className="viewClassesEyebrow">Instructor</p>
                      <h5>{instructor?.employeeName || 'N/A'}</h5>
                      <p>{instructor?.employeePhone || 'Phone not set'}</p>
                    </div>
                  </div>

                  <div className="viewClassesInfoGrid">
                    <div className="viewClassesInfoCard">
                      <span>Cohort date</span>
                      <strong>{classData?.intakeDate || 'Not set'}</strong>
                    </div>
                    <div className="viewClassesInfoCard">
                      <span>Start time</span>
                      <strong>{classData?.startTime || 'Not set'}</strong>
                    </div>
                    <div className="viewClassesInfoCard">
                      <span>End time</span>
                      <strong>{classData?.endTime || 'Not set'}</strong>
                    </div>
                    <div className="viewClassesInfoCard">
                      <span>Students</span>
                      <strong>{students.length}</strong>
                    </div>
                  </div>

                  <div className="viewClassesSideActions">
                    <Button className="viewClassesPrimaryButton w-100" onClick={() => setShowModal(true)}>
                      <FontAwesomeIcon icon={faAdd} className="me-2" />
                      Add Students
                    </Button>
                    <Button className="viewClassesSecondaryButton w-100" variant="outline-dark" onClick={() => setShowInstructorModal(true)}>
                      <FontAwesomeIcon icon={faUserEdit} className="me-2" />
                      Switch Instructor
                    </Button>
                    <Button
                      className="viewClassesDangerButton w-100"
                      variant="danger"
                      onClick={handleDeleteClass}
                      disabled={deletingClass}
                    >
                      <FontAwesomeIcon icon={faTrashCan} className="me-2" />
                      {deletingClass ? 'Deleting...' : 'Delete Class'}
                    </Button>
                  </div>
                </aside>
              </section>
            </>
          )}
        </div>

        {/* Add Student Modal */}
        <Modal show={showModal} onHide={() => setShowModal(false)} centered>
          <Modal.Header closeButton>
            <Modal.Title>Add Student to Class</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>Search for a student</Form.Label>
              <Form.Control
                type="text"
                placeholder="Type student name"
                value={searchValue}
                onChange={handleSearchChange}
              />
              {results.length > 0 && (
                <div className="searchResultsStud" ref={searchResultsRef}>
                  {results.map((result, index) => (
                    <div
                      key={index}
                      className="resultItem"
                      onClick={() => onSelectStudent(result.userId)}
                    >
                      <div className="resultItemDiv">
                        <img
                          src={result.profilePic}
                          alt={`${result.bio}'s profile`}
                          width={40}
                          height={40}
                        />
                      </div>
                      <div>
                        <p>{result.bio}</p>
                        <p className="statusResult">{result.userStatus}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Form.Group>

            <Table striped bordered hover>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Status</th>
                  <th>Course</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {selectedStudent && (
                  <tr>
                    <td>{selectedStudent.bio}</td>
                    <td>{selectedStudent.userStatus}</td>
                    <td>{selectedStudent.course}</td>
                    <td>
                      <Button variant="success" size="sm" onClick={handleAddStudent}>
                        Add
                      </Button>
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowModal(false)}>
              Close
            </Button>
          </Modal.Footer>
        </Modal>

        {/* Switch Instructor Modal */}
        <Modal show={showInstructorModal} onHide={() => setShowInstructorModal(false)} centered>
          <Modal.Header closeButton>
            <Modal.Title>Switch Instructor</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form.Group>
              <Form.Label>Search Instructor</Form.Label>
              <Form.Control
                type="text"
                placeholder="Type instructor name"
                value={instructorSearch}
                onChange={handleInstructorSearch}
              />
            </Form.Group>

            {instructorResults.length > 0 && (
              <Table striped bordered hover className="mt-3">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {instructorResults.map(inst => (
                    <tr key={inst.id}>
                      <td>{inst.employeeName}</td>
                      <td>{inst.employeeEmail}</td>
                      <td>
                        <Button
                          variant="success"
                          size="sm"
                          onClick={() => handleSwitchInstructor(inst)}
                        >
                          Assign
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowInstructorModal(false)}>
              Close
            </Button>
          </Modal.Footer>
        </Modal>
      </div>
    </div>
  );
}

export default ViewAllClasses;
